import { ERA_LOOKS, leaderboard } from '@continuum/show-core';
import type { BoardSource } from '@continuum/show-core';
import { PostLine, mono, sans } from './atoms.js';
import { BottomSheet } from './BottomSheet.js';
import { SCORE_COPY, fmt } from './scoreText.js';
import { postName } from './screenText.js';
import type { Lang } from './text.js';

const YEARS = { 1: '2026', 2: '2213', 3: '2398', 4: '2617', 5: '2791' } as const;
/** The era's own colour, except the surface green, which is lifted to read on the dark card. */
const eraColour = (chapter: 1 | 2 | 3 | 4 | 5) => (chapter === 5 ? '#8fb77a' : ERA_LOOKS[chapter].acc);

/**
 * The end of an era on a guest's phone: the whole table ranked by that era's game, their own
 * line lit, and where they stand over the evening. A sheet like every other: pull it down to
 * put it away, and SCORES on the toolbar brings it back. The big screen shows the same board.
 */
export function BoardCard({
  lang,
  state,
  chapter,
  seat,
  accent,
  final,
  onAway,
}: {
  lang: Lang;
  state: BoardSource;
  chapter: 1 | 2 | 3 | 4 | 5;
  seat: number;
  accent: string;
  /** At docking: the last board of the evening. */
  final: boolean;
  onAway: () => void;
}) {
  const s = SCORE_COPY[lang];
  const rows = leaderboard(state, chapter);
  const voyage = leaderboard(state, 'all');
  const mine = rows.find((r) => r.seat === seat);
  const mineOverall = voyage.find((r) => r.seat === seat);
  const shared = (rank: number) => rows.filter((r) => r.rank === rank).length > 1;
  const scored = rows.some((r) => r.points > 0);

  return (
    <BottomSheet
      id="scores"
      size="full"
      onDismiss={onAway}
      closeLabel={`▾ ${s.hide}`}
      label={s.boardTitle}
      tone={{ bg: '#090814', fg: '#efeaff', border: `${accent}88`, grip: accent }}
      header={
        <>
          <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.8, color: eraColour(chapter) }}>
            {YEARS[chapter]} · {s.eraName[chapter]}
          </div>
          <div style={{ marginTop: 4, fontFamily: sans, fontSize: 24, fontWeight: 700, letterSpacing: 2 }}>{s.boardTitle}</div>
          <div style={{ marginTop: 2, marginBottom: 8, fontFamily: mono, fontSize: 13, lineHeight: 1.45, color: '#a79ed0' }}>{s.boardHow}</div>
        </>
      }
      footer={
        (mine && scored) || (mineOverall && chapter > 1) ? (
          <div>
            {mine && scored && (
              <div style={{ fontFamily: sans, fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: accent }}>
                {mine.points > 0 ? s.boardYou(mine.rank, rows.length, mine.points, shared(mine.rank)) : s.boardNone(final)}
              </div>
            )}
            {mineOverall && chapter > 1 && (
              <div style={{ marginTop: 4, fontFamily: mono, fontSize: 13, color: '#a79ed0' }}>
                {s.boardVoyage(mineOverall.rank, mineOverall.points, final)}
              </div>
            )}
          </div>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!scored && <div style={{ fontFamily: mono, fontSize: 13, color: '#a79ed0', padding: '8px 0' }}>{s.boardEmpty}</div>}
        {rows.map((row) => {
          const you = row.seat === seat;
          return (
            <div
              key={row.seat}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 40,
                padding: '0 10px',
                borderRadius: 9,
                background: you ? `${accent}2e` : 'rgba(255,255,255,.04)',
                border: `1px solid ${you ? accent : 'transparent'}`,
              }}
            >
              <span style={{ width: 24, fontFamily: mono, fontSize: 13, color: '#a79ed0', textAlign: 'right' }}>{row.rank}</span>
              <span style={{ flex: 1, minWidth: 0, padding: '4px 0' }}>
                <span style={{ display: 'block', fontFamily: sans, fontSize: 18, fontWeight: 700, letterSpacing: 1.5, lineHeight: 1.2 }}>
                  {row.callsign}
                  {you && <span style={{ marginLeft: 8, fontFamily: mono, fontSize: 11, letterSpacing: 1.5, color: accent }}>{s.you}</span>}
                </span>
                <PostLine color="#8a82b3">{postName(state.seats.find((x) => x.seat === row.seat)?.profession, lang)}</PostLine>
              </span>
              <span style={{ fontFamily: mono, fontSize: 14, color: you ? '#efeaff' : '#c9c1ee' }}>{fmt(row.points)}</span>
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
}
